import React, { useState, useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface ComboItem {
  code: string;
  label: string;
  depth?: number;   // indentation level for tree options (crops); default 0
}

interface Props {
  items: ComboItem[];
  value: string[];                 // selected codes
  onChange: (codes: string[]) => void;
  ariaLabel: string;               // accessible name for the closed-row button
  placeholder?: string;            // closed-row empty text
  maxChips?: number;               // default 3
}

const MultiSelectCombo: React.FC<Props> = ({ items, value, onChange, ariaLabel, placeholder, maxChips = 3 }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const controlRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const byCode = useMemo(() => new Map(items.map(i => [i.code, i])), [items]);

  const toggle = (code: string) => {
    onChange(value.includes(code) ? value.filter(c => c !== code) : [...value, code]);
  };

  useLayoutEffect(() => {
    if (!open) { setCoords(null); setQuery(''); return; }
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom, left: rect.left, width: rect.width });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (controlRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); controlRef.current?.focus(); }
    };
    // The panel is position:fixed, so ancestor/page scroll would make it drift —
    // close on that. But the panel has its OWN scrollable list; scrolling inside
    // it (wheel or scrollbar) must NOT close the panel. The scroll listener is
    // capturing, so it also sees the inner-list scroll — ignore those by target.
    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (target && panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => i.label.toLowerCase().includes(q) || i.code.toLowerCase().includes(q));
  }, [items, query]);

  const selectedItems = value.map(code => byCode.get(code) ?? { code, label: code });
  const shownChips = selectedItems.slice(0, maxChips);
  const overflow = selectedItems.length - shownChips.length;

  const panelStyle: React.CSSProperties = coords
    ? { position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.width }
    : { visibility: 'hidden' };

  return (
    <div className="pat-combo">
      <button
        ref={controlRef}
        type="button"
        className="pat-combo__control"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
      >
        {selectedItems.length === 0 ? (
          <span className="pat-combo__placeholder">
            {placeholder ?? t('common.multiselect.placeholder', { defaultValue: 'Select…' })}
          </span>
        ) : (
          <>
            {shownChips.map(i => <span key={i.code} className="pat-combo__chip">{i.label}</span>)}
            {overflow > 0 && (
              <span className="pat-combo__more">
                {t('common.multiselect.moreCount', { count: overflow, defaultValue: '+{{count}} more' })}
              </span>
            )}
          </>
        )}
        <span className="pat-combo__caret" aria-hidden="true">▾</span>
      </button>

      {open && createPortal(
        <div ref={panelRef} className="pat-combo__panel" style={panelStyle} role="listbox" aria-multiselectable="true">
          <input
            ref={searchRef}
            type="text"
            className="pat-input pat-combo__search"
            placeholder={t('common.multiselect.searchPlaceholder', { defaultValue: 'Search…' })}
            aria-label={t('common.multiselect.searchPlaceholder', { defaultValue: 'Search…' })}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="pat-combo__list">
            {filtered.length === 0 ? (
              <div className="pat-combo__empty">
                {t('common.multiselect.noOptions', { defaultValue: 'No options' })}
              </div>
            ) : (
              filtered.map(i => (
                <label
                  key={i.code}
                  className="pat-combo__option"
                  style={i.depth ? { paddingLeft: `${i.depth * 16 + 12}px` } : undefined}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(i.code)}
                    onChange={() => toggle(i.code)}
                    aria-label={i.label}
                  />
                  <span>{i.label}</span>
                </label>
              ))
            )}
          </div>
          {value.length > 0 && (
            <div className="pat-combo__summary">
              {t('common.multiselect.selectedCount', { count: value.length, defaultValue: '{{count}} selected' })}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiSelectCombo;
